import type { RunSummary } from "contracts/api/crawl/run-read.responses";
import { type RunStage, runStages } from "contracts/api/crawl/run-stages";
import { CRAWL_RUN_STATUS, isActiveCrawlRunStatus } from "contracts/enums/core";
import type { RunStageKey } from "contracts/enums/run-stage";
import { PIPELINE_STAGE_STATE } from "contracts/enums/run-stage";
import type { PipelineStep } from "../../ui/PipelineStepper";

// How the derived run stages are worded. Surface copy for this screen — no
// backend-rendered artifact names a stage — so it stays here rather than in the
// package the backend and workers also depend on.
const STAGE_LABELS: Record<RunStageKey, string> = {
  crawl: "Crawling",
  fetch: "Fetching",
  analyse: "Analysing",
  canonicalize: "Consolidating",
};

function stageDetail(stage: RunStage): string | undefined {
  if (stage.total !== null) return `${stage.done}/${stage.total}`;
  return stage.done > 0 ? String(stage.done) : undefined;
}

export function runPipelineSteps(run: RunSummary): PipelineStep[] {
  return runStages(run).map((stage) => ({
    key: stage.key,
    label: STAGE_LABELS[stage.key],
    state: stage.state,
    detail: stageDetail(stage),
    failed: stage.failed,
  }));
}

/**
 * The single one-line reading of where a run is, shared by the top bar, the run
 * list and the run detail header. Previously each derived its own wording, and
 * the top bar's ignored `canonicalizing` — so a run wedged in the consolidate
 * hop read as "Analysing", the exact ambiguity that flag exists to remove.
 */
export function runProgressLabel(run: RunSummary): string {
  if (!isActiveCrawlRunStatus(run.status)) {
    return run.status === CRAWL_RUN_STATUS.COMPLETED ? "Completed" : "Failed";
  }
  const stages = runStages(run);
  const current = [...stages]
    .reverse()
    .find((stage) => stage.state !== PIPELINE_STAGE_STATE.WAITING);
  if (!current) return "Starting scan";
  const label = STAGE_LABELS[current.key];
  const detail = stageDetail(current);
  return detail ? `${label} ${detail}` : label;
}
